import React, { useState } from 'react';
import { 
  ShieldCheck, User, MapPin, Mail, CreditCard, 
  Calendar, Phone, Lock, Smartphone, MessageSquare, 
  Cpu, Globe, ShieldAlert 
} from 'lucide-react';

/**
 * 🛰️ SAHELGUARD KYC INTERFACE - V200.0.0
 * Registre Souverain : Collecte des données pour Police & Assurances.
 * Système de verrouillage matériel SgDeviceID intégré.
 */

const KYCForm = ({ onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    userFirstName: '',
    userLastName: '',
    birthDate: '',
    email: '',
    whatsapp: '',
    country: 'Burkina Faso',
    city: '',
    address: '',
    cniNumber: '',
    cniIssueDate: '',
    cniIssuePlace: '',
    userPin: '', // 6 chiffres (Admin) ou 4 chiffres (Standard)
    sosNumber1: '',
    sosNumber2: '',
    sosNumber3: '',
    imei1: '',
    vendorWhatsapp: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- LOGIQUE DE VALIDATION DU PIN (Règle 2026-02-05) ---
    const pinLength = formData.userPin.length;
    if (pinLength !== 4 && pinLength !== 6) {
      alert("🚨 ERREUR SÉCURITÉ : Le PIN doit comporter 4 chiffres (Standard) ou 6 chiffres (Super Admin).");
      return;
    }

    setIsSubmitting(true);
    
    // 🔗 Point d'entrée de ton Worker Cloudflare Réel
    const EMPIRE_API_URL = "https://sg-core-api.sahelguard.workers.dev/api/sale/execute"; 

    const payload = {
      reseller_id: "ZACA_01",
      pack_amount: 40000,
      token_id: "SG-TEST-777-BFA", 
      imei: formData.imei1,
      nom_client: `${formData.userFirstName} ${formData.userLastName}`,
      kyc_metadata: {
        ...formData,
        scellage_date: new Date().toISOString(),
        protocol_v: "200.0.0"
      }
    };

    try {
      const response = await fetch(EMPIRE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`🚨 PROTOCOLE RÉUSSI\n\nL'abonné est inscrit au registre souverain.\nIMEI Scellé : ${formData.imei1}\nPropriétaire : ${payload.nom_client}`);
        if (onSubmitSuccess) onSubmitSuccess({ ...result, profile: formData });
      } else {
        alert("❌ ÉCHEC CRITIQUE : " + (result.error || "Erreur de scellage D1"));
      }
    } catch (error) {
      console.error("Erreur de signal :", error);
      alert("📡 ERREUR SIGNAL : Le Worker Cloudflare est injoignable.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-[#0a0a0a] border border-white/10 rounded-[2.5rem] p-8 md:p-12 shadow-2xl animate-in fade-in zoom-in duration-500 mb-20 relative overflow-hidden">
      
      <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none">
        <ShieldAlert size={300} className="text-emerald-500" />
      </div>

      <div className="flex items-center justify-between mb-10 border-b border-white/5 pb-8 relative z-10">
        <div className="flex items-center gap-4">
          <div className="bg-emerald-500/20 p-3 rounded-xl border border-emerald-500/20">
            <ShieldCheck className="text-emerald-500" size={32} />
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-tighter uppercase italic text-white leading-none">Registre Souverain</h2>
            <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] mt-1">Full KYC & Security Protocol</p>
          </div>
        </div>
        <div className="hidden md:block text-right">
          <div className="text-[9px] font-black text-emerald-500/50 uppercase tracking-widest">Signal Status</div>
          <div className="text-xs font-mono text-emerald-500 flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            ENCRYPTION_ACTIVE
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-12 relative z-10">
        
        {/* 01. IDENTITÉ LÉGALE */}
        <div className="space-y-6">
          <h3 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em] flex items-center gap-2 border-l-2 border-emerald-500 pl-4">
            <User size={12}/> 01. Identité Légale
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="Prénom" icon={<User size={16}/>} value={formData.userFirstName} onChange={(e) => setFormData({...formData, userFirstName: e.target.value})} placeholder="Ex: Moussa" />
            <InputField label="Nom" icon={<User size={16}/>} value={formData.userLastName} onChange={(e) => setFormData({...formData, userLastName: e.target.value})} placeholder="Ex: Traoré" />
            <InputField label="Email" type="email" icon={<Mail size={16}/>} value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="moussa@yarmotek.com" />
            <InputField label="Date de Naissance" type="date" icon={<Calendar size={16}/>} value={formData.birthDate} onChange={(e) => setFormData({...formData, birthDate: e.target.value})} />
          </div>
        </div>

        {/* 02. DOCUMENTS */}
        <div className="space-y-6">
          <h3 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em] flex items-center gap-2 border-l-2 border-emerald-500 pl-4">
            <CreditCard size={12}/> 02. Pièce d'Identité (Registre Officiel)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="N° CNI / Passport" icon={<CreditCard size={16}/>} value={formData.cniNumber} onChange={(e) => setFormData({...formData, cniNumber: e.target.value})} placeholder="B12345678" />
            <InputField label="Date Délivrance" type="date" icon={<Calendar size={16}/>} value={formData.cniIssueDate} onChange={(e) => setFormData({...formData, cniIssueDate: e.target.value})} />
            <InputField label="Lieu Délivrance" icon={<MapPin size={16}/>} value={formData.cniIssuePlace} onChange={(e) => setFormData({...formData, cniIssuePlace: e.target.value})} placeholder="Ex: Ouagadougou" />
          </div>
        </div>

        {/* 03. LOCALISATION */}
        <div className="space-y-6">
          <h3 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em] flex items-center gap-2 border-l-2 border-emerald-500 pl-4">
            <Globe size={12}/> 03. Contact & Résidence
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField label="WhatsApp Personnel" icon={<MessageSquare size={16}/>} value={formData.whatsapp} onChange={(e) => setFormData({...formData, whatsapp: e.target.value})} placeholder="+226XXXXXXXX" />
            <InputField label="Pays" icon={<Globe size={16}/>} value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} />
            <InputField label="Ville" icon={<MapPin size={16}/>} value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="Ex: Bobo-Dioulasso" />
            <InputField label="Adresse" icon={<MapPin size={16}/>} value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Secteur, Porte, Rue..." />
          </div>
        </div>

        {/* 04. UNITÉ DE SÉCURITÉ */}
        <div className="space-y-6 bg-white/[0.02] p-8 rounded-[2rem] border border-white/5">
          <h3 className="text-[10px] font-black text-amber-500 uppercase tracking-[0.4em] flex items-center gap-2 mb-6">
            <Lock size={12}/> 04. Sécurité & Clés de Voûte (Strict)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <InputField label="N° Secours 1" icon={<Phone size={16}/>} value={formData.sosNumber1} onChange={(e) => setFormData({...formData, sosNumber1: e.target.value})} placeholder="Ex: +226..." />
            <InputField label="N° Secours 2" icon={<Phone size={16}/>} value={formData.sosNumber2} onChange={(e) => setFormData({...formData, sosNumber2: e.target.value})} placeholder="Ex: +226..." />
            <InputField label="N° Secours 3" icon={<Phone size={16}/>} value={formData.sosNumber3} onChange={(e) => setFormData({...formData, sosNumber3: e.target.value})} placeholder="Ex: +226..." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField label="PIN (4 ou 6 chiffres)" type="password" icon={<Lock size={16}/>} placeholder="Secret" value={formData.userPin} onChange={(e) => setFormData({...formData, userPin: e.target.value})} />
            <InputField label="IMEI Principal (*#06#)" icon={<Smartphone size={16}/>} value={formData.imei1} onChange={(e) => setFormData({...formData, imei1: e.target.value})} placeholder="IMEI Obligatoire" />
            <InputField label="WhatsApp Revendeur" icon={<MessageSquare size={16}/>} value={formData.vendorWhatsapp} onChange={(e) => setFormData({...formData, vendorWhatsapp: e.target.value})} placeholder="ID Boutique" />
          </div>
          
          <div className="mt-8 bg-black/50 border border-white/5 rounded-2xl p-5 flex items-center justify-between shadow-inner">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <Cpu className="text-emerald-500" size={24} />
              </div>
              <div>
                <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest leading-none mb-1">SgDeviceID Engine</div>
                <div className="text-[10px] text-white font-mono font-bold tracking-tighter uppercase">Hardware_Lock_D1_Linked</div>
              </div>
            </div>
            <div className="text-right hidden sm:block">
                <div className="text-[8px] font-black text-slate-600 uppercase">Cryptage de Voûte</div>
                <div className="text-[10px] text-emerald-500 font-black">SHA-512 / AES-256</div>
            </div>
          </div>
        </div>

        <button 
          type="submit"
          disabled={isSubmitting}
          className={`w-full ${isSubmitting ? 'bg-slate-800 cursor-not-allowed opacity-50' : 'bg-emerald-600 hover:bg-emerald-500 active:scale-[0.98]'} text-white py-8 rounded-3xl font-black text-[11px] uppercase tracking-[0.4em] shadow-2xl shadow-emerald-900/40 transition-all border border-emerald-400/20 flex items-center justify-center gap-4`}
        >
          {isSubmitting ? "SCELLAGE D1 EN COURS..." : "Finaliser l'Inscription & Sceller l'Unité"}
        </button>
      </form>
    </div>
  );
};

const InputField = ({ label, icon, type = "text", placeholder, value, onChange }) => (
  <div className="space-y-2 group">
    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1 group-focus-within:text-emerald-500 transition-colors">{label}</label>
    <div className="relative">
      {icon && <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-emerald-500 transition-colors">{icon}</div>}
      <input 
        type={type}
        required
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full bg-[#050505] border border-white/5 rounded-2xl py-5 ${icon ? 'pl-12' : 'px-5'} pr-5 text-[13px] focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all text-white placeholder:text-slate-800 shadow-inner font-bold`}
      />
    </div>
  </div>
);

export default KYCForm;